import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Key, Plus, Trash2, Copy, Check, Eye, EyeOff } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@core/components/ui/card";
import { Button } from "@core/components/ui/button";
import { Input } from "@core/components/ui/input";
import { useAuth } from "@core/contexts/AuthContext";
import { supabase } from "@core/lib/supabase";
import AccountPageTemplate from "./AccountPageTemplate";

type ApiKey = {
  id: string;
  name: string;
  is_active: boolean;
  last_used_at: string | null;
  created_at: string;
};

type NewKeyResult = {
  id: string;
  plaintext: string;
};

export default function AccountApiKeys() {
  const { t, i18n } = useTranslation("common");
  const { user } = useAuth();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState<NewKeyResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);

  useEffect(() => {
    loadKeys();
  }, []);

  async function loadKeys() {
    setLoading(true);
    const { data, error } = await supabase
      .from("api_keys")
      .select("id, name, is_active, last_used_at, created_at")
      .order("created_at", { ascending: false });

    if (!error && data) setKeys(data);
    setLoading(false);
  }

  async function generateKey(): Promise<string> {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return (
      "ddk_" +
      Array.from(bytes)
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("")
    );
  }

  async function hashKey(key: string): Promise<string> {
    const encoded = new TextEncoder().encode(key);
    const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }

  async function handleCreate() {
    if (!newKeyName.trim() || !user) return;
    setCreating(true);
    try {
      const plaintext = await generateKey();
      const keyHash = await hashKey(plaintext);

      const { data, error } = await supabase
        .from("api_keys")
        .insert({
          user_id: user.id,
          name: newKeyName.trim(),
          key_hash: keyHash,
        })
        .select("id")
        .single();

      if (error || !data) throw error;

      setNewKeyResult({ id: data.id, plaintext });
      setNewKeyName("");
      setShowForm(false);
      await loadKeys();
    } catch {
      // ignore — user sees no feedback, stays in form
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id: string) {
    setRevoking(id);
    await supabase.from("api_keys").update({ is_active: false }).eq("id", id);
    await loadKeys();
    setRevoking(null);
  }

  async function copyToClipboard(text: string) {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString(i18n.language || "fr", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }

  return (
    <AccountPageTemplate
      title={t("account.api_keys.title")}
      description={t("account.api_keys.description")}
      testId="account-api-keys-page"
    >
      <div className="space-y-6">
        {/* Clé nouvellement créée — à copier immédiatement */}
        {newKeyResult && (
          <Card className="border-emerald-700/40 bg-emerald-900/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-emerald-300 text-base">
                <Key className="w-4 h-4" />
                {t("account.api_keys.new_key.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-emerald-200/80">
                {t("account.api_keys.new_key.warning")}
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded bg-gray-900 px-3 py-2 text-sm font-mono text-emerald-300 break-all">
                  {showKey
                    ? newKeyResult.plaintext
                    : "•".repeat(newKeyResult.plaintext.length)}
                </code>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowKey((v) => !v)}
                  data-testid="toggle-show-key"
                >
                  {showKey ? (
                    <EyeOff className="w-4 h-4 text-gray-400" />
                  ) : (
                    <Eye className="w-4 h-4 text-gray-400" />
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 border-emerald-700/60 text-emerald-300 hover:bg-emerald-900/40"
                  onClick={() => copyToClipboard(newKeyResult.plaintext)}
                  data-testid="copy-new-key-button"
                >
                  {copied ? (
                    <Check className="w-3.5 h-3.5" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                  {copied
                    ? t("account.api_keys.copied")
                    : t("account.api_keys.copy")}
                </Button>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-gray-400"
                onClick={() => setNewKeyResult(null)}
                data-testid="dismiss-new-key"
              >
                {t("account.api_keys.new_key.dismiss")}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Liste des clés */}
        <Card className="border-gray-800 bg-gray-900/80">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-white">
              <Key className="w-5 h-5 text-blue-400" />
              {t("account.api_keys.active_keys")}
            </CardTitle>
            {!showForm && (
              <Button
                size="sm"
                className="gap-1.5 bg-blue-600 hover:bg-blue-700"
                onClick={() => setShowForm(true)}
                data-testid="create-api-key-button"
              >
                <Plus className="w-4 h-4" />
                {t("account.api_keys.new_key_button")}
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Formulaire création */}
            {showForm && (
              <div className="flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800/60 p-3">
                <Input
                  placeholder={t("account.api_keys.name_placeholder")}
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                  className="flex-1 border-gray-600 bg-gray-900 text-white"
                  autoFocus
                  data-testid="new-key-name-input"
                />
                <Button
                  size="sm"
                  onClick={handleCreate}
                  disabled={creating || !newKeyName.trim()}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="confirm-create-key-button"
                >
                  {creating
                    ? t("account.api_keys.creating")
                    : t("account.api_keys.create")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowForm(false);
                    setNewKeyName("");
                  }}
                  data-testid="cancel-create-key-button"
                >
                  {t("account.api_keys.cancel")}
                </Button>
              </div>
            )}

            {/* Clés existantes */}
            {loading ? (
              <p className="text-sm text-gray-500" data-testid="keys-loading">
                {t("account.api_keys.loading")}
              </p>
            ) : keys.filter((k) => k.is_active).length === 0 ? (
              <p
                className="text-sm text-gray-500"
                data-testid="no-keys-message"
              >
                {t("account.api_keys.empty")}
              </p>
            ) : (
              <div className="divide-y divide-gray-800">
                {keys
                  .filter((k) => k.is_active)
                  .map((key) => (
                    <div
                      key={key.id}
                      className="flex items-center justify-between py-3"
                      data-testid={`api-key-row-${key.id}`}
                    >
                      <div>
                        <p className="text-sm font-medium text-white">
                          {key.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {t("account.api_keys.created_on", {
                            date: formatDate(key.created_at),
                          })}
                          {key.last_used_at && (
                            <>
                              {" "}
                              {t("account.api_keys.last_used", {
                                date: formatDate(key.last_used_at),
                              })}
                            </>
                          )}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:bg-red-950/40 hover:text-red-300 gap-1.5"
                        onClick={() => handleRevoke(key.id)}
                        disabled={revoking === key.id}
                        data-testid={`revoke-key-${key.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        {revoking === key.id
                          ? t("account.api_keys.revoking")
                          : t("account.api_keys.revoke")}
                      </Button>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Guide d'utilisation */}
        <Card className="border-gray-800 bg-gray-900/80">
          <CardHeader>
            <CardTitle className="text-sm text-gray-300">
              {t("account.api_keys.usage.title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-gray-400">
            <p>
              {t("account.api_keys.usage.add_config_before")}{" "}
              <code className="rounded bg-gray-800 px-1 py-0.5 text-xs text-gray-300">
                claude_desktop_config.json
              </code>{" "}
              :
            </p>
            <pre className="overflow-x-auto rounded-lg bg-gray-950 p-3 text-xs text-gray-300">
              {`{
  "mcpServers": {
    "doodates": {
      "command": "npx",
      "args": ["-y", "@doodates/mcp-server"],
      "env": {
        "DOODATES_API_KEY": "${t("account.api_keys.usage.key_placeholder")}"
      }
    }
  }
}`}
            </pre>
            <p>
              {t("account.api_keys.usage.replace_before")}{" "}
              <code className="rounded bg-gray-800 px-1 py-0.5 text-xs text-gray-300">
                {t("account.api_keys.usage.key_placeholder")}
              </code>{" "}
              {t("account.api_keys.usage.replace_after")}
            </p>
          </CardContent>
        </Card>
      </div>
    </AccountPageTemplate>
  );
}
